/**
 * srtToAss.ts
 *
 * Pure SRT → ASS (Advanced SubStation Alpha) conversion used by the client-side
 * video export (videoExport.ts) to burn subtitles in via ffmpeg.wasm's `ass=`
 * filter (libass + freetype; the wasm core has NO fontconfig, so fonts are
 * resolved from the `fontsdir` folder by their internal family name).
 *
 * Deliberately store-free: the caption style is passed as a structural param
 * (same shape as captionStyleStore state) so the function stays unit-testable.
 *
 * R2 model: size is absolute px referenced to 1080p (`captionSizePx`) and the
 * position is a freeform `{ xPct, yPct }` anchor. The ASS PlayRes follows both
 * dimensions of the source frame; using a fixed PlayResX would make libass
 * stretch the glyphs horizontally on 720p, 1440p, 4K, portrait, or ultrawide
 * video. Every Dialogue line carries
 * a `{\an5\pos(X,Y)}` override placing the caption center at the exact anchor
 * (X = xPct% of PlayResX, Y = yPct% of PlayResY) — Alignment is fixed at 5
 * (center) and the Style margins stay 0.
 */
import type { SrtEntry } from '@lambda-libs/types';

export type CaptionStyleName = 'white-outline' | 'white-box' | 'yellow-outline';

export type CaptionFont = 'system' | 'classic2';

/** Freeform subtitle anchor as a percentage of the video frame (0–100). */
export interface CaptionPositionPct {
  xPct: number;
  yPct: number;
}

/** Source/display frame dimensions used as the ASS script resolution. */
export interface AssVideoSize {
  width: number;
  height: number;
}

/**
 * Structural caption style input — pass the captionStyleStore state
 * `{ font, captionSizePx, position, style, lineSpacingPct?, boxWidthPct?, boxHeightPct? }`
 * straight through.
 */
export interface CaptionStyleOptions {
  font: CaptionFont;
  captionSizePx: number;
  position: CaptionPositionPct;
  style: CaptionStyleName;
  /**
   * Emphasis. bold → Style Bold flag (absent = legacy: bold iff classic2,
   * since the bundled face is the Bold cut). italic → Style Italic flag PLUS
   * a per-dialogue `\fax` shear, because the bundled font ships no italic
   * face for libass to select — without the shear the burn-in would render
   * upright while the editor overlay shows browser-synthesized italics.
   */
  bold?: boolean;
  italic?: boolean;
  /**
   * R5 — outline stroke thickness as a % of the caption font size (CapCut-style,
   * resolution-independent). Burned as the ASS Style Outline width in scaled
   * px: round(strokePct/100 × Fontsize). Absent = default 4 (≈ the legacy
   * fixed Outline 2 at the default 54px size). Ignored by the opaque-box style.
   */
  strokePct?: number;
  /**
   * Vertical line spacing as a percentage (100 = default). Explicit lines are
   * positioned independently so spacing changes never distort glyph height.
   */
  lineSpacingPct?: number;
  /**
   * R4 — explicit text-box width as a percentage of the frame (null/absent =
   * auto). Burned as symmetric per-dialogue MarginL/MarginR so libass wraps
   * at exactly this width regardless of the \pos anchor — matching the
   * editor overlay. Height has no ASS equivalent (the renderer derives it
   * from font size + line count) and is overlay-only.
   */
  boxWidthPct?: number | null;
  boxHeightPct?: number | null;
}

/** ASS colours in &HAABBGGRR format. */
export const ASS_COLORS = {
  white: '&H00FFFFFF',
  yellow: '&H0000D7FF',
  blackOutline: '&H00000000',
  // 70% opaque black — matches the overlay's rgba(0,0,0,0.7) white-box fill
  // (alpha 0x4D ≈ 30% transparent; &H80 would be 50%).
  boxBackground: '&H4D000000',
  secondary: '&H000000FF',
} as const;

/**
 * Legacy family of UI/src/assets/fonts/ProximaNova-Bold.ttf (TTF nameID 1).
 * The built-in libass provider used by ffmpeg.wasm indexes nameID 1 and 4,
 * but not the typographic-family nameID 16 (`Proxima Nova`). Its matching is
 * exact apart from case, so using the typographic family silently renders no
 * text even though the font file loads successfully.
 */
export const PROXIMA_NOVA_FONT_NAME = 'Proxima Nova Rg';
/**
 * Browser FFmpeg cannot read fonts installed on the customer's computer.
 * Export therefore maps the editor's `system` option to the bundled face as
 * a deterministic fallback; requesting Arial would again produce no text.
 */
export const SYSTEM_FONT_NAME = PROXIMA_NOVA_FONT_NAME;

export const DEFAULT_PLAY_RES_X = 1920;
export const DEFAULT_PLAY_RES_Y = 1080;
/** captionSizePx is referenced to this frame height. */
export const REFERENCE_HEIGHT = 1080;

/**
 * Faux-italic shear for the burn-in: 0.25 ≈ tan(14°), the slant browsers apply
 * when synthesizing an oblique from an upright face (CSS font-synthesis), so
 * the burn-in matches the editor overlay. `\fax` shifts each glyph's outline
 * horizontally by fax × its height, positive leaning the tops right. Applied
 * only when `italic` is on (see srtToAss): the Italic Style flag alone is not
 * enough because no italic face is bundled for libass to select.
 */
export const FAUX_ITALIC_SHEAR = 0.25;

export interface AssDecoration {
  primaryColour: string;
  outlineColour: string;
  backColour: string;
  /** 1 = outline, 3 = opaque box. */
  borderStyle: number;
  outline: number;
}

export function resolveAssDecoration(style: CaptionStyleName): AssDecoration {
  switch (style) {
    case 'white-box':
      return {
        primaryColour: ASS_COLORS.white,
        outlineColour: ASS_COLORS.blackOutline,
        backColour: ASS_COLORS.boxBackground,
        borderStyle: 3,
        outline: 0,
      };
    case 'yellow-outline':
      return {
        primaryColour: ASS_COLORS.yellow,
        outlineColour: ASS_COLORS.blackOutline,
        backColour: ASS_COLORS.blackOutline,
        borderStyle: 1,
        outline: 2,
      };
    case 'white-outline':
    default:
      return {
        primaryColour: ASS_COLORS.white,
        outlineColour: ASS_COLORS.blackOutline,
        backColour: ASS_COLORS.blackOutline,
        borderStyle: 1,
        outline: 2,
      };
  }
}

/**
 * ASS timestamp `h:mm:ss.cc` (centiseconds, single-digit hour).
 * Negative / non-finite input clamps to 0:00:00.00.
 */
export function assTimestamp(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const centis = Math.round(safe * 100);
  const h = Math.floor(centis / 360000);
  const m = Math.floor((centis % 360000) / 6000);
  const s = Math.floor((centis % 6000) / 100);
  const c = centis % 100;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(c).padStart(2, '0')}`;
}

/** Convert newlines (\r\n, \r, \n) to the ASS hard line break `\N`. */
export function escapeAssText(text: string): string {
  return text.replace(/\r\n?/g, '\n').split('\n').join('\\N');
}

/**
 * ASS Fontsize from the user's absolute caption size (px @1080p), scaled to
 * the actual PlayResY so the burn-in matches the editor overlay.
 */
export function computeFontsize(captionSizePx: number, playResY: number): number {
  const size =
    Number.isFinite(captionSizePx) && captionSizePx > 0 ? captionSizePx : REFERENCE_HEIGHT * 0.05;
  return Math.round((size * playResY) / REFERENCE_HEIGHT);
}

/**
 * Per-dialogue positioning override: `{\an5\pos(X,Y)}` with the caption
 * centered on the anchor (xPct/yPct of the frame).
 */
export function computePosTag(position: CaptionPositionPct, playResX: number, playResY: number): string {
  const x = Number.isFinite(position?.xPct) ? position.xPct : 50;
  const y = Number.isFinite(position?.yPct) ? position.yPct : 88;
  return `{\\an5\\pos(${Math.round((x / 100) * playResX)},${Math.round((y / 100) * playResY)})}`;
}

/**
 * R4/R6 — symmetric dialogue margin forcing libass to wrap at the editor's
 * box width. libass breaks lines inside [MarginL, PlayResX − MarginR] BEFORE
 * placing the block at the \pos anchor, so equal side margins pin the wrap
 * width regardless of where the caption is anchored.
 *
 * `paddingPx` is the box's total horizontal text padding (the overlay wraps
 * inside `.subtitle-text` padding, so the export must subtract it too —
 * otherwise export lines run wider than the editor's). null/invalid width →
 * margin 0 (the caller substitutes the auto-mode cap before calling).
 */
export function computeWrapMargin(
  boxWidthPct: number | null | undefined,
  playResX: number,
  paddingPx = 0
): number {
  if (boxWidthPct == null || !Number.isFinite(boxWidthPct) || boxWidthPct <= 0) return 0;
  const boxWidth = (Math.min(100, boxWidthPct) / 100) * playResX;
  const wrapWidth = Math.max(0, boxWidth - Math.max(0, paddingPx));
  return Math.max(0, Math.round((playResX - wrapWidth) / 2));
}

/** Line-spacing clamp range (percent; mirrors the store's 60–160). */
const LINE_SPACING_MIN = 60;
const LINE_SPACING_MAX = 160;
const LINE_SPACING_DEFAULT = 100;

/**
 * Clamp the editor's vertical line-spacing percentage without applying it to
 * glyph geometry. ASS ScaleY scales the letters themselves, unlike CSS
 * line-height, so custom spacing is implemented with per-line positions.
 */
export function normalizeLineSpacingPct(lineSpacingPct: number | null | undefined): number {
  if (lineSpacingPct == null || !Number.isFinite(lineSpacingPct)) {
    return LINE_SPACING_DEFAULT;
  }
  return Math.round(
    Math.min(LINE_SPACING_MAX, Math.max(LINE_SPACING_MIN, lineSpacingPct))
  );
}

/**
 * Center explicitly-authored subtitle lines around the same anchor as the
 * editor. CSS uses a 1.2 line-height for custom spacing; each ASS Dialogue is
 * kept at ScaleY 100 and only its Y position changes.
 */
export function computeLinePosTags(
  position: CaptionPositionPct,
  playResX: number,
  playResY: number,
  fontsize: number,
  lineCount: number,
  lineSpacingPct: number | null | undefined
): string[] {
  const count = Math.max(1, Math.round(lineCount));
  if (count === 1) return [computePosTag(position, playResX, playResY)];

  const xPct = Number.isFinite(position?.xPct) ? position.xPct : 50;
  const yPct = Number.isFinite(position?.yPct) ? position.yPct : 88;
  const x = Math.round((xPct / 100) * playResX);
  const anchorY = (yPct / 100) * playResY;
  const lineAdvance = 1.2 * fontsize * (normalizeLineSpacingPct(lineSpacingPct) / 100);
  const firstY = anchorY - ((count - 1) * lineAdvance) / 2;

  return Array.from(
    { length: count },
    (_, index) => `{\\an5\\pos(${x},${Math.round(firstY + index * lineAdvance)})}`
  );
}

/** Outline stroke limits (% of font size) — mirrors the store's 0–25 / default 4. */
const STROKE_PCT_MIN = 0;
const STROKE_PCT_MAX = 25;
const STROKE_PCT_DEFAULT = 4;

/**
 * R6 — overlay parity constants. Must mirror the editor overlay CSS
 * (SrtEditor.vue): auto-size captions cap at 90% of the frame width
 * (`maxWidth: 90%` on the positioning shell) and `.subtitle-text` carries
 * `0.3em` padding on every side — the burn-in has to wrap inside the same
 * padding, and the white-box style's background extends by it.
 */
export const AUTO_WRAP_WIDTH_PCT = 90;
export const BOX_PADDING_EM = 0.3;

/**
 * R5 — outline stroke (% of the font size) → ASS Style Outline width in
 * scaled px. `ScaledBorderAndShadow: yes` + PlayResY = probed height make the
 * outline 1:1 px at export resolution, and libass paints it fully OUTSIDE the
 * glyph — the same visible width as the overlay (whose centered webkit stroke
 * is doubled and paint-ordered, see outlineStrokeCss in captionStyleStore).
 * Absent/non-finite → the 4% default (≈ legacy Outline 2 at 54px).
 */
export function computeOutlineWidth(
  strokePct: number | null | undefined,
  fontsize: number
): number {
  const pct =
    strokePct == null || !Number.isFinite(strokePct)
      ? STROKE_PCT_DEFAULT
      : Math.min(STROKE_PCT_MAX, Math.max(STROKE_PCT_MIN, strokePct));
  return Math.max(0, Math.round((pct / 100) * fontsize));
}

/**
 * Resolve the ASS script resolution. A numeric third argument is retained for
 * compatibility with the original height-only API and therefore keeps the
 * historical 1920 PlayResX fallback. The export path passes both dimensions,
 * which prevents non-uniform horizontal font scaling in libass.
 */
export function resolvePlayRes(videoSize: AssVideoSize | number | undefined): AssVideoSize {
  if (typeof videoSize === 'number') {
    return {
      width: DEFAULT_PLAY_RES_X,
      height:
        Number.isFinite(videoSize) && videoSize > 0
          ? Math.round(videoSize)
          : DEFAULT_PLAY_RES_Y,
    };
  }

  const height =
    Number.isFinite(videoSize?.height) && (videoSize?.height ?? 0) > 0
      ? Math.round(videoSize!.height)
      : DEFAULT_PLAY_RES_Y;
  const width =
    Number.isFinite(videoSize?.width) && (videoSize?.width ?? 0) > 0
      ? Math.round(videoSize!.width)
      : Math.round((height * DEFAULT_PLAY_RES_X) / DEFAULT_PLAY_RES_Y);
  return { width, height };
}

/**
 * Convert SRT entries to an ASS document.
 *
 * @param entries     SRT entries (times in seconds).
 * @param style       Caption style (font / size / position / decoration).
 * @param videoSize Source frame width/height probed at export time. A numeric
 *                  height is accepted for backward compatibility. Invalid or
 *                  absent dimensions fall back to a 1920×1080-equivalent
 *                  script resolution.
 */
export function srtToAss(
  entries: SrtEntry[],
  style: CaptionStyleOptions,
  videoSize: AssVideoSize | number = {
    width: DEFAULT_PLAY_RES_X,
    height: DEFAULT_PLAY_RES_Y,
  }
): string {
  const { width, height } = resolvePlayRes(videoSize);

  const decoration = resolveAssDecoration(style.style);
  const fontname = style.font === 'classic2' ? PROXIMA_NOVA_FONT_NAME : SYSTEM_FONT_NAME;
  // bold absent = legacy behaviour (the bundled Proxima Nova face is the Bold
  // cut, so classic2 burned Bold -1 before the toggle existed).
  const bold = (style.bold ?? style.font === 'classic2') ? -1 : 0;
  const italic = style.italic ? -1 : 0;
  const fontsize = computeFontsize(style.captionSizePx, height);
  // Alignment 5 (center) + per-dialogue \pos pin the caption to the freeform
  // anchor — no alignment-per-position mapping, no MarginV bands.
  const posTag = computePosTag(style.position, width, height);
  // Italic burn-in: Style flag + per-dialogue \fax shear (no italic face is
  // bundled, so the flag alone would render upright — see FAUX_ITALIC_SHEAR).
  const italicTag = italic ? `{\\fax${FAUX_ITALIC_SHEAR}}` : '';
  // R4/R6 — wrap width burns in as per-dialogue margins. Auto-size captions
  // get the editor's 90% cap (the overlay's maxWidth); an explicit box width
  // wraps inside its own text padding (`.subtitle-text` padding: 0.3em).
  const boxPaddingPx = 2 * BOX_PADDING_EM * fontsize;
  const wrapMargin = computeWrapMargin(
    style.boxWidthPct ?? AUTO_WRAP_WIDTH_PCT,
    width,
    boxPaddingPx
  );
  // R5 — outline stroke (% of font size) burns as the scaled Outline width.
  // R6 — for the opaque-box style (borderStyle 3) libass uses Outline as the
  // background padding around the text; 0.3em mirrors the overlay's padding,
  // so the burned box hugs the text exactly like the editor's.
  const outlineWidth =
    decoration.borderStyle === 3
      ? Math.round(BOX_PADDING_EM * fontsize)
      : computeOutlineWidth(style.strokePct, fontsize);

  const lines: string[] = [];
  lines.push('[Script Info]');
  lines.push('Title: Transcriptor subtitles');
  lines.push('ScriptType: v4.00+');
  // R6 — WrapStyle 1 = greedy end-of-line word wrap: fill each line up to the
  // margin before breaking, exactly like the browser CSS wrapping the editor
  // overlay shows (WrapStyle 0 is libass "smart" balancing — different breaks).
  lines.push('WrapStyle: 1');
  lines.push('ScaledBorderAndShadow: yes');
  lines.push(`PlayResX: ${width}`);
  lines.push(`PlayResY: ${height}`);
  lines.push('');
  lines.push('[V4+ Styles]');
  lines.push(
    'Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding'
  );
  lines.push(
    `Style: Default,${fontname},${fontsize},${decoration.primaryColour},${ASS_COLORS.secondary},` +
      `${decoration.outlineColour},${decoration.backColour},${bold},${italic},0,0,100,100,0,0,` +
      `${decoration.borderStyle},${outlineWidth},0,5,0,0,0,1`
  );
  lines.push('');
  lines.push('[Events]');
  lines.push('Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text');

  for (const entry of entries) {
    if (!entry || !entry.text || !entry.text.trim()) continue; // empty captions would render empty boxes
    if (!(entry.endTime > entry.startTime)) continue; // degenerate timings
    const textLines = entry.text.replace(/\r\n?/g, '\n').split('\n');
    const hasCustomLineSpacing =
      textLines.length > 1 && normalizeLineSpacingPct(style.lineSpacingPct) !== 100;

    if (hasCustomLineSpacing) {
      const linePosTags = computeLinePosTags(
        style.position,
        width,
        height,
        fontsize,
        textLines.length,
        style.lineSpacingPct
      );
      textLines.forEach((textLine, index) => {
        if (!textLine.trim()) return;
        lines.push(
          `Dialogue: 0,${assTimestamp(entry.startTime)},${assTimestamp(entry.endTime)},Default,,` +
            `${wrapMargin},${wrapMargin},0,,` +
            linePosTags[index] +
            italicTag +
            textLine
        );
      });
    } else {
      lines.push(
        `Dialogue: 0,${assTimestamp(entry.startTime)},${assTimestamp(entry.endTime)},Default,,` +
          `${wrapMargin},${wrapMargin},0,,` +
          posTag +
          italicTag +
          escapeAssText(entry.text)
      );
    }
  }
  lines.push('');
  return lines.join('\n');
}
